import { captureEvent } from "@Light/app/analytics";
import { useFeature } from "@Light/app/features";
import {
  useMarkOptOutRatePageSeen,
  useOptOutPlanTracking,
} from "@Light/components/optOutSurvey";
import { useAccount } from "@Light/components/page/account";
import {
  providerFaq,
  RateFaqs,
  RateOffer,
  TermsAcceptance,
  type RateFaq,
} from "@Light/components/rateOffer";
import { useScaffold } from "@Light/scaffold";
import { useLight } from "@Light/services/light";
import type {
  LocationPlan,
  PlanHighlight,
  PlanResponse,
} from "@Light/services/lightTypes";
import { AcceptRenewalPlanRequest } from "@Light/services/lightTypes";
import { formatDisplayDate, parseLocalDate } from "@Light/utils/dateHelpers";
import { formatDecimal } from "@Light/utils/formatting";
import { useMutation } from "@Light/utils/mutation";
import { useOptionalRate } from "@Light/utils/plan";
import { planFeaturesForRateGroup } from "@Light/utils/planFeatures";
import { isMatchedSolarBuyback } from "@Light/utils/solarBuyback";
import { getUtilityBehavior } from "@Light/utils/utility";
import { useEffect, useState, type ReactNode } from "react";
import {
  renewalHighlightsToValueProps,
  shownRenewalHighlights,
} from "./renewalHighlights";

export type RenewRateOfferProps = {
  currentPlan: LocationPlan;
  onBack?: () => void;
  highlights?: PlanHighlight[];
};

export function RenewRateOffer({
  currentPlan,
  onBack,
  highlights,
}: RenewRateOfferProps) {
  const { useAcceptRenewalPlanMutation } = useLight();
  const acceptRate = useMutation<AcceptRenewalPlanRequest>(
    useAcceptRenewalPlanMutation,
  );
  const rate = useOptionalRate();
  const location = useAccount().locations?.[0];
  useMarkOptOutRatePageSeen();
  useOptOutPlanTracking(rate?.uuid);

  const hasMatchedRenewalLanguage = useFeature(
    "CUSTOMER_CLIENT_MATCHED_RENEWAL_LANGUAGE",
  );
  const isMatchedToUnmatched =
    !!rate &&
    isMatchedSolarBuyback(currentPlan) &&
    !isMatchedSolarBuyback(rate);
  const showBuybackFaqs = isMatchedToUnmatched && hasMatchedRenewalLanguage;
  const faqs = useRenewalFaqs(rate, showBuybackFaqs);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const valueProps = renewalHighlightsToValueProps(
    highlights,
    rate?.presentation.value_propositions,
  );

  useEffect(() => {
    for (const highlight of shownRenewalHighlights(highlights)) {
      captureEvent("renewal_highlight_shown", { ...highlight });
    }
  }, [highlights]);

  const mutate = () => {
    if (!rate || !termsAccepted) return;
    captureEvent("renewal_accepted", {
      efl_uuid: rate.uuid,
      group_uuid: rate.group_uuid,
    });
    acceptRate.mutate({
      plan_uuid: rate.uuid,
      location_uuid: location.uuid,
      service_start_date: rate.earliest_start_date,
      terms_accepted: true,
    });
  };

  return (
    <RateOffer
      rate={rate}
      features={planFeaturesForRateGroup(rate ?? currentPlan)}
      planName={(rate ?? currentPlan).name}
      terms={[(rate ?? currentPlan).term_months]}
      selectedTerm={(rate ?? currentPlan).term_months}
      onSelectTerm={() => {}}
      onContinue={mutate}
      mutation={rate ? acceptRate : { isLoading: true }}
      continueButtonText="Accept new plan"
      continueDisabled={!rate || !termsAccepted}
      errorMessage={
        acceptRate.isError
          ? "Error accepting new plan. Please try again later."
          : null
      }
      onBack={onBack}
      faqs={<RateFaqs faqs={faqs} />}
      beforeContinue={
        <RenewalTermsAcceptance
          checked={rate ? termsAccepted : false}
          onChange={rate ? setTermsAccepted : () => {}}
        />
      }
      valueProps={valueProps}
      utilityBehavior={getUtilityBehavior(location?.tdsp_id ?? undefined)}
    />
  );
}

function RenewalTermsAcceptance({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (accepted: boolean) => void;
}): ReactNode {
  return (
    <TermsAcceptance checked={checked} onChange={onChange}>
      I have read, understand and accept the disclosures and terms of service of
      Light, PUCT #10340.
    </TermsAcceptance>
  );
}

function useRenewalFaqs(
  rate: PlanResponse | undefined,
  showBuybackFaqs: boolean,
): RateFaq[] {
  const scaffold = useScaffold();
  const startDate = rate
    ? formatDisplayDate(parseLocalDate(rate.earliest_start_date))
    : undefined;
  const buybackRate = rate
    ? formatDecimal(rate.export_rate_cents_per_kwh)
    : undefined;

  const faqs: RateFaq[] = [
    {
      question: "What do I need to do to renew?",
      answer: (
        <>
          Review your new plan and accept it. We’ll take care of the rest — no
          interruption to your service.
        </>
      ),
    },
    {
      question: "When does my new plan start?",
      answer: (
        <>
          Your new plan starts on the first meter reading after{" "}
          {startDate ? startDate : "your contractual start date"}. Until then,
          your current rate stays in effect.
        </>
      ),
    },
    providerFaq(scaffold.page.companyName),
  ];

  if (showBuybackFaqs) {
    faqs.push(
      {
        question: "What’s changed in the plan?",
        answer: (
          <>
            Market buyback rates have significantly decreased, so we’re
            adjusting to keep your grid costs low. This means you’ll earn{" "}
            {buybackRate}¢ per kWh to keep your overall bill changes minimal.
          </>
        ),
      },
      {
        question: "Can I modify my plan offer?",
        answer: (
          <>
            If you still want a matched buyback rate, reach out using the
            contact options on this page and we’ll be happy to assist.
          </>
        ),
      },
    );
  }

  return faqs;
}
